import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import LogoImage from './LogoImage';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';

const STUDENT_PORTAL_URL = 'https://portal.sciencesorted.co.uk';

const navItems = [
  { to: '/', label: 'Home' },
  { to: '/courses', label: 'Courses' },
  { to: '/services', label: 'How we teach' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { CTA } = useSite();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? 'bg-white/95 backdrop-blur border-b border-black/10 shadow-sm'
          : 'bg-white border-b border-transparent'
      }`}
    >
      <div className={`container flex items-center justify-between ${scrolled ? 'py-2' : 'py-3'}`}>
        <Link
          to="/"
          className="flex items-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2 rounded"
          aria-label="Science Sorted - home"
        >
          <LogoImage
            className={`w-auto transition-all group-hover:-rotate-1 ${
              scrolled ? 'h-10 md:h-14' : 'h-11 md:h-16'
            }`}
          />
        </Link>

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-8 mt-1.5">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm font-semibold hover-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2 rounded px-1 py-1 ${
                  isActive ? 'text-brand-ink' : 'text-brand-ink/70 hover:text-brand-ink'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={STUDENT_PORTAL_URL}
            className="inline-flex items-center gap-1.5 rounded-full border border-brand-ink/25 bg-white px-4 h-10 text-sm font-semibold text-brand-ink hover:bg-brand-butter hover:border-brand-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2"
            data-testid="student-portal-desktop"
          >
            <LogIn className="w-4 h-4" aria-hidden="true" />
            Student Portal
          </a>
          <Button
            asChild
            className="bg-brand-sun text-brand-ink hover:bg-brand-ink hover:text-brand-sun rounded-full font-semibold shadow-sm"
          >
            <a
              href={BOOK_TRIAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="gap-1"
            >
              {CTA.primary} <ArrowUpRight className="w-4 h-4" />
            </a>
          </Button>
        </div>

        <button
          className="lg:hidden p-2 rounded-lg hover:bg-brand-butter focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink focus-visible:ring-offset-2"
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer - rendered at header root so its `position: fixed`
          is not clipped/mis-sized by the sticky <header> containing block.
          Uses explicit calc height instead of `bottom-0` for reliability. */}
      {open && (
        <div
          id="mobile-nav"
          className="lg:hidden fixed left-0 right-0 top-[56px] h-[calc(100vh-56px)] bg-white z-40 overflow-y-auto border-t border-black/10"
          data-testid="mobile-nav-drawer"
        >
          <nav aria-label="Mobile" className="container py-6 flex flex-col gap-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-lg text-lg font-semibold ${
                    isActive ? 'bg-brand-butter text-brand-ink' : 'text-brand-ink hover:bg-brand-butter'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-2">
              <a
                href={STUDENT_PORTAL_URL}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/25 bg-white h-12 text-brand-ink font-semibold hover:bg-brand-butter hover:border-brand-ink transition-colors"
                data-testid="student-portal-mobile"
              >
                <LogIn className="w-4 h-4" aria-hidden="true" />
                Student Portal
              </a>
              <Button
                asChild
                className="bg-brand-sun text-brand-ink hover:bg-brand-ink hover:text-brand-sun rounded-full font-semibold h-12"
              >
                <a
                  href={BOOK_TRIAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="gap-1"
                >
                  {CTA.primary} <ArrowUpRight className="w-4 h-4" />
                </a>
              </Button>
              <Button asChild variant="outline" className="rounded-full h-12 border-brand-ink/20">
                <Link to="/courses">{CTA.secondary}</Link>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
